// pages/Settings.js
import React, { useState, useEffect, useRef, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiLoader, FiUser, FiLock, FiSettings, FiTrash2, FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PageHeader from '../components/layout/PageHeader';
import { pageDescriptions } from '../data/pageDescriptions';

const Settings = () => {
  const { currentUser, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    default_polish_instructions: '',
    auto_cleanup_enabled: false,
    auto_cleanup_jobs_days: 90,
    auto_cleanup_applications_days: 90
  });
  
  const [saving, setSaving] = useState(false);
  const [savingPolish, setSavingPolish] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteModalTitleId = useId();
  const deleteButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);

  // Focus the Cancel button when the delete modal opens
  useEffect(() => {
    if (showDeleteModal && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [showDeleteModal]);
  
  useEffect(() => {
    if (currentUser) {
      setFormData(prevData => ({
        ...prevData,
        full_name: currentUser.full_name || '',
        email: currentUser.email || '',
        default_polish_instructions: currentUser.default_polish_instructions || '',
        auto_cleanup_enabled: currentUser.auto_cleanup_enabled ?? false,
        auto_cleanup_jobs_days: currentUser.auto_cleanup_jobs_days ?? 90,
        auto_cleanup_applications_days: currentUser.auto_cleanup_applications_days ?? 90
      }));
    }
  }, [currentUser]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Mock API call - replace with actual endpoint
       await api.patch('/api/auth/profile', {
         full_name: formData.full_name.trim()
       });
      
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    try {
      setSaving(true);
      
      // Mock API call - replace with actual endpoint
       await api.post('/api/auth/change-password', {
         current_password: formData.current_password,
         new_password: formData.new_password
       });
      
      toast.success('Password changed successfully');
      
      setFormData({
        ...formData,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePolishSettingsUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setSavingPolish(true);
      await api.patch('/api/auth/profile', {
        default_polish_instructions: formData.default_polish_instructions
      });
      
      toast.success('Polish instructions updated successfully');
    } catch (error) {
      console.error('Error updating polish instructions:', error);
      toast.error('Failed to update polish instructions');
    } finally {
      setSavingPolish(false);
    }
  };

  const [savingCleanup, setSavingCleanup] = useState(false);
  const handleAutoCleanupUpdate = async (e) => {
    e.preventDefault();
    const jobsDays = Math.max(1, parseInt(formData.auto_cleanup_jobs_days, 10) || 90);
    const applicationsDays = Math.max(1, parseInt(formData.auto_cleanup_applications_days, 10) || 90);
    try {
      setSavingCleanup(true);
      await api.patch('/api/auth/profile', {
        auto_cleanup_enabled: formData.auto_cleanup_enabled,
        auto_cleanup_jobs_days: jobsDays,
        auto_cleanup_applications_days: applicationsDays
      });
      setFormData(prev => ({ ...prev, auto_cleanup_jobs_days: jobsDays, auto_cleanup_applications_days: applicationsDays }));
      await refreshUser();
      toast.success('Auto cleanup settings saved. They will run each time you log in.');
    } catch (error) {
      console.error('Error updating auto cleanup:', error);
      toast.error('Failed to update auto cleanup settings');
    } finally {
      setSavingCleanup(false);
    }
  };
  
  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      await api.delete(`/api/auth/account/${currentUser.id}`);
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleCloseDeleteModal = () => {
    if (!deleting) {
      setShowDeleteModal(false);
      deleteButtonRef.current?.focus();
    }
  };
  
  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title={pageDescriptions['/settings'].title}
        description={pageDescriptions['/settings'].description}
        whatYouCanDo={pageDescriptions['/settings'].whatYouCanDo}
        whatYouShouldDo={pageDescriptions['/settings'].whatYouShouldDo}
      />
      
      {/* Profile Settings */}
      <div className="card overflow-hidden mb-6">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 flex items-center">
          <FiUser className="h-5 w-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">Profile Information</h2>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <form onSubmit={handleProfileUpdate}>
            <div className="space-y-6">
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    id="full_name"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={formData.full_name}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-50"
                    value={formData.email}
                    disabled
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Email cannot be changed. Contact support if you need to update your email.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="-ml-1 mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {/* Mentor Information - Only show for mentees */}
      <div className="card overflow-hidden mb-6">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 flex items-center">
          <FiSettings className="h-5 w-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">Resume Polish Defaults</h2>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <form onSubmit={handlePolishSettingsUpdate}>
            <div className="space-y-4">
              <div>
                <label htmlFor="default_polish_instructions" className="block text-sm font-medium text-gray-700">
                  Default instructions for polishing
                </label>
                <textarea
                  id="default_polish_instructions"
                  name="default_polish_instructions"
                  rows={4}
                  className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Example: Keep tone concise, emphasize impact metrics, avoid changing company names."
                  value={formData.default_polish_instructions}
                  onChange={handleInputChange}
                />
                <p className="mt-1 text-xs text-gray-500">
                  These instructions will be applied by default when polishing your resume.
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  disabled={savingPolish}
                >
                  {savingPolish ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="-ml-1 mr-2 h-4 w-4" />
                      Save Defaults
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Auto Cleanup */}
      <div className="card overflow-hidden mb-6">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 flex items-center">
          <FiZap className="h-5 w-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">Auto Cleanup</h2>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <p className="text-sm text-gray-600 mb-4">
            Automatically remove old saved jobs (that you haven&apos;t applied to) and mark old applications as Rejected each time you log in. You can also clean manually on the Saved Jobs and Applications pages.
          </p>
          <form onSubmit={handleAutoCleanupUpdate}>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="auto_cleanup_enabled"
                  name="auto_cleanup_enabled"
                  type="checkbox"
                  checked={formData.auto_cleanup_enabled}
                  onChange={(e) => setFormData({ ...formData, auto_cleanup_enabled: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="auto_cleanup_enabled" className="ml-2 block text-sm text-gray-900">
                  Enable auto cleanup on login
                </label>
              </div>
              {formData.auto_cleanup_enabled && (
                <>
                  <div>
                    <label htmlFor="auto_cleanup_jobs_days" className="block text-sm font-medium text-gray-700">
                      Remove saved jobs (unapplied) older than (days)
                    </label>
                    <input
                      id="auto_cleanup_jobs_days"
                      name="auto_cleanup_jobs_days"
                      type="number"
                      min={1}
                      value={formData.auto_cleanup_jobs_days}
                      onChange={(e) => setFormData({ ...formData, auto_cleanup_jobs_days: e.target.value })}
                      className="mt-1 block w-24 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="auto_cleanup_applications_days" className="block text-sm font-medium text-gray-700">
                      Mark as Rejected applications (still in Applied) older than (days)
                    </label>
                    <input
                      id="auto_cleanup_applications_days"
                      name="auto_cleanup_applications_days"
                      type="number"
                      min={1}
                      value={formData.auto_cleanup_applications_days}
                      onChange={(e) => setFormData({ ...formData, auto_cleanup_applications_days: e.target.value })}
                      className="mt-1 block w-24 rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </>
              )}
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  disabled={savingCleanup}
                >
                  {savingCleanup ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="-ml-1 mr-2 h-4 w-4" />
                      Save Auto Cleanup
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Mentor Information - Only show for mentees */}
      {currentUser && currentUser.mentor && (
        <div className="card overflow-hidden mb-6">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 flex items-center">
            <FiUser className="h-5 w-5 text-primary-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Your Mentor</h2>
          </div>
          
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                <FiUser className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">{currentUser.mentor.full_name}</h3>
                <p className="text-sm text-gray-500">{currentUser.mentor.email}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500">
                Your mentor is here to help guide you through your job search journey. Feel free to reach out with any questions or for advice.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Password Settings */}
      <div className="card overflow-hidden mb-6">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 flex items-center">
          <FiLock className="h-5 w-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">Password</h2>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          {!showPasswordForm ? (
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Change Password</h3>
                <p className="text-sm text-gray-500">
                  Update your password for increased security
                </p>
              </div>
              <button
                type="button"
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={() => setShowPasswordForm(true)}
              >
                Change Password
              </button>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <input
                  type="password"
                  name="current_password"
                  id="current_password"
                  className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  value={formData.current_password}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  name="new_password"
                  id="new_password"
                  className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  value={formData.new_password}
                  onChange={handleInputChange}
                  required
                  minLength={8}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Password must be at least 8 characters long
                </p>
              </div>
              
              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirm_password"
                  id="confirm_password"
                  className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => setShowPasswordForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      
      {/* Danger Zone */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6 bg-red-50 flex items-center">
          <FiTrash2 className="h-5 w-5 text-red-400 mr-2" />
          <h2 className="text-lg font-medium text-red-800">Danger Zone</h2>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Delete Account</h3>
              <p className="text-sm text-gray-500">
                Permanently delete your account and all associated data
              </p>
            </div>
            <button
              ref={deleteButtonRef}
              type="button"
              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>
      
      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby={deleteModalTitleId}
          onKeyDown={(e) => { if (e.key === 'Escape') handleCloseDeleteModal(); }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 opacity-75"
              aria-hidden="true"
              onClick={handleCloseDeleteModal}
            />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl border border-gray-200 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiTrash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 id={deleteModalTitleId} className="text-lg leading-6 font-medium text-gray-900">
                      Delete Account
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete your account? All of your data will be permanently removed.
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" aria-hidden="true" />
                      Deleting...
                    </>
                  ) : 'Delete'}
                </button>
                <button
                  ref={cancelButtonRef}
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={handleCloseDeleteModal}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;